<template>
  <div class="fg-place-list">
    <p v-if="!places.length" class="fg-empty">{{ t('place.empty') }}</p>

    <div v-if="kinds.length > 1" class="fg-place-list__filters">
      <button type="button" class="fg-chip" :class="{ 'is-active': filter === 'all' }" @click="filter = 'all'">{{ t('place.all') }}</button>
      <button
        v-for="kind in kinds"
        :key="kind"
        type="button"
        class="fg-chip"
        :class="{ 'is-active': filter === kind }"
        @click="filter = kind"
      >{{ kindLabel(kind) }}</button>
    </div>

    <div v-if="visible.length" class="fg-place-rows">
      <button
        v-for="place in visible"
        :key="place.id"
        type="button"
        class="fg-place-row"
        :class="{ 'is-selected': selected?.id === place.id }"
        @click="pick(place)"
      >
        <span class="fg-place-row__radio" aria-hidden="true"></span>
        <span class="fg-place-row__icon"><Icon :name="iconForType(place.type)" :size="16" /></span>
        <span class="fg-place-row__main">
          <span class="fg-place-row__name">{{ humanizeLabel(place.name || place.code) }}</span>
          <span class="fg-place-row__meta">
            <span class="fg-place-row__kind">{{ kindLabel(place.type) }}</span>
            <span v-if="place.use === 'Shared'" class="fg-place-row__kind">{{ t('place.shared') }}</span>
            <span>{{ t('place.capacity', { n: place.capacity }) }}</span>
            <span v-if="hasAmenity(place.in_board?.has_window)" class="fg-place-row__feat"><Icon name="window" :size="11" /> {{ t('place.features.window') }}</span>
            <span v-if="hasAmenity(place.in_board?.has_shower)" class="fg-place-row__feat"><Icon name="shower" :size="11" /> {{ t('place.features.shower') }}</span>
            <span v-if="hasAmenity(place.in_board?.has_toilet)" class="fg-place-row__feat"><Icon name="toilet" :size="11" /> {{ t('place.features.toilet') }}</span>
          </span>
        </span>
        <span v-if="place.price !== undefined" class="fg-place-row__price">
          {{ formatPrice(place.price, place.currency ?? 'EUR', locale) }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStore } from '../../store'
import { useI18n } from '../../i18n'
import { availabilitiesForGroup, groupKey, pickCheapestTariff, sailingKey, tariffDisplayAmount, tariffDisplayCurrency } from '../../../../shared/rules'
import type { GroupNumber } from '../../../../shared/rules'
import type { Place } from '../../../../shared/types'
import { formatPrice, humanizeLabel } from '../../utils/format'
import { hasAmenity } from '../../../../../../shared/utils/normalizers'
import Icon from '../Icon.vue'

interface PlaceWithPrice extends Place {
  price?: number
  currency?: string
}

const props = withDefaults(
  defineProps<{ direction: 'outward' | 'return'; group?: GroupNumber }>(),
  { group: 1 },
)

const store = useStore()
const { t, locale } = useI18n()
const filter = ref<string>('all')

const slotKey = computed(() => groupKey(props.group))

// Places du sailing sélectionné, restreintes au sous-groupe passagers courant
// (group_id). Un dossier > 4 pax expose 2 PlaceList (group 1 puis group 2).
const places = computed<PlaceWithPrice[]>(() => {
  const slot = store.availabilities[props.direction]
  if (!slot) return []
  const sailingId = store.selectedSailing[props.direction]
  let pool = slot.all ?? []
  // Toutes les availabilities du sailing sélectionné (Balearia : 1 par place) —
  // filtre par clé de sailing (depart_at + company), PAS par av.id, sinon seule
  // la 1ʳᵉ availability (donc 1 seule place) passerait.
  if (sailingId) pool = pool.filter((av) => sailingKey(av) === sailingId)
  // Restreint au sous-groupe (group_id) : group 1 vs group 2.
  pool = availabilitiesForGroup(pool, props.group)
  const seen = new Set<string>()
  const merged: PlaceWithPrice[] = []
  for (const av of pool) {
    const tariff = pickCheapestTariff(av)
    // tariff null → on garde price/currency undefined (place sans prix triée en
    // fin), donc on ne route via les helpers shared que si un tariff existe.
    const price = tariff ? tariffDisplayAmount(tariff) : undefined
    const currency = tariff ? tariffDisplayCurrency(tariff) : undefined
    for (const p of av.places ?? []) {
      if (!p.id || seen.has(p.id)) continue
      seen.add(p.id)
      merged.push({ ...p, price, currency })
    }
  }
  // Tri par prix croissant (les moins chères d'abord) ; place sans prix en fin.
  return merged.sort((a, b) => (a.price ?? Number.POSITIVE_INFINITY) - (b.price ?? Number.POSITIVE_INFINITY))
})

const kinds = computed<string[]>(() => {
  const unique = new Set<string>()
  for (const p of places.value) if (p.type) unique.add(p.type)
  return Array.from(unique)
})

const visible = computed(() => {
  if (filter.value === 'all') return places.value
  return places.value.filter((p) => p.type === filter.value)
})

const selected = computed(() => store.selectedPlaces[props.direction][slotKey.value])

// Reset filter quand on change de sailing (les places peuvent ne plus matcher).
watch(() => store.selectedSailing[props.direction], () => { filter.value = 'all' })

function pick(place: Place): void { store.selectedPlaces[props.direction][slotKey.value] = place }

// Cabine / suite / lit (en cabine partagée) → icône cabine ; fauteuil / pont → siège.
function iconForType(type: string): 'seat' | 'cabin' {
  return ['cabin', 'suite', 'bed'].includes(type?.toLowerCase() ?? '') ? 'cabin' : 'seat'
}

const KIND_LABELS: Record<string, string> = {
  seat: 'place.seat', cabin: 'place.cabin', suite: 'place.suite', bed: 'place.bed', pont: 'place.deck',
}

function kindLabel(type: string): string {
  const key = KIND_LABELS[type?.toLowerCase() ?? '']
  return key ? t(key) : type
}
</script>
